import { Link } from "react-router-dom";
import {
    ShieldCheck,
    Search,
    ClipboardCheck,
    LineChart,
    FileText,
    Users,
    Umbrella,
    HardHat,
    Eye,
    CalendarClock,
    History,
    Building,
    CloudLightning,
    Scale,
    ArrowRight,
    CheckCircle2,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import GuardianMark from "../components/GuardianMark";
import useReveal from "../hooks/useReveal";
import { SingleReviewBlock, REVIEWS } from "../components/ClientReviews";
import usePageMeta from "../hooks/usePageMeta";

/* ──────────────────────────────────────────────────────────────────
 *  /guardian-program  —  Flagship page.
 *  The emotional centre of the SRM website.
 * ────────────────────────────────────────────────────────────────── */

const EYEBROW =
    "font-display font-bold tracking-[0.26em] uppercase text-srm-gold text-[12px] lg:text-[13px]";
const GOLD_RULE = "block h-[3px] w-16 bg-srm-gold";

/* Four Guardianship Program stages per v2.0 corporate release.
   Assessment (entry) \u2192 Scope & Tender \u2192 Delivery Oversight \u2192 Ongoing Guardianship. */
const PILLARS = [
    {
        title: "Assessment & Report",
        subtitle: "Stage 1 \u00b7 The starting point",
        blurb: "Establish the actual condition of the roof and provide an evidence-based plan the committee can act on.",
        icon: Search,
        items: [
            { icon: Search, label: "Independent on-roof assessment" },
            { icon: ClipboardCheck, label: "Photographic findings & defect register" },
            { icon: Scale, label: "Risk & priority classification" },
            { icon: FileText, label: "Practical committee-ready recommendations" },
        ],
    },
    {
        title: "Scope of Works & Tender",
        subtitle: "Stage 2 \u00b7 Independent procurement",
        blurb: "Define exactly what is required so contractors can price the same work on a genuinely comparable basis.",
        icon: ClipboardCheck,
        items: [
            { icon: ClipboardCheck, label: "Detailed independent scope of works" },
            { icon: Users, label: "Suitable contractors identified & pre-checked" },
            { icon: FileText, label: "Tender issue, queries & apples-to-apples review" },
            { icon: Scale, label: "Independent recommendation to the client" },
        ],
    },
    {
        title: "Delivery Oversight",
        subtitle: "Stage 3 \u00b7 Independent oversight of the works",
        blurb: "Ensure the selected work is delivered against the agreed scope, with independent monitoring of quality, variations and claims.",
        icon: Eye,
        items: [
            { icon: HardHat, label: "Materials & methodology review" },
            { icon: Eye, label: "Progress oversight & photographic records" },
            { icon: ClipboardCheck, label: "Variation & payment claim review" },
            { icon: CheckCircle2, label: "Practical completion & close-out" },
        ],
    },
    {
        title: "Ongoing Guardianship & Monitoring",
        subtitle: "Stage 4 \u00b7 Long-term care",
        blurb: "Annual assessment cycles, condition monitoring and long-term planning \u2014 the roof managed as an asset, not addressed only after failure.",
        icon: CalendarClock,
        items: [
            { icon: CalendarClock, label: "Annual roof assessment cycles" },
            { icon: LineChart, label: "Maintenance & sinking-fund planning" },
            { icon: Umbrella, label: "Insurance-readiness documentation" },
            { icon: History, label: "Committee & manager guidance across the years" },
        ],
    },
];

const PROTECTS = [
    {
        icon: Building,
        title: "The Asset",
        body: "Evidence-based intervention timed to when the roof genuinely needs attention — supporting extended service life.",
    },
    {
        icon: Users,
        title: "The Committee",
        body: "A documented record of assessments, findings and decisions that supports better-documented committee decisions over time.",
    },
    {
        icon: Umbrella,
        title: "The Claim",
        body: "An independent condition baseline and photographic history that supports insurance and maintenance records.",
    },
    {
        icon: LineChart,
        title: "The Budget",
        body: "Planned, sequenced expenditure aligned to the sinking fund — assists long-term budget planning and reduces the likelihood of unexpected expenditure.",
    },
];

function Hero() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="guardian-program-hero"
            className="relative isolate w-full overflow-hidden bg-srm-navyDark"
        >
            <div className="absolute inset-0 -z-10">
                <img
                    src="/assets/hero-background.jpg"
                    alt=""
                    aria-hidden="true"
                    className="h-full w-full object-cover object-center opacity-40"
                    loading="eager"
                    decoding="async"
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(15,40,86,0.92) 0%, rgba(15,40,86,0.78) 100%)",
                    }}
                />
            </div>

            <div className="container-srm relative py-28 lg:py-40">
                <div className="max-w-[820px] mx-auto text-center reveal">
                    <div className="font-display font-bold tracking-[0.26em] uppercase text-srm-gold text-[12px] lg:text-[13px]">
                        The Guardianship Journey
                    </div>
                    <span aria-hidden className={`mx-auto mt-5 ${GOLD_RULE}`} />
                    <h1
                        data-testid="guardian-program-headline"
                        className="mt-10 font-display font-bold text-[2.5rem] sm:text-[3.1rem] lg:text-[3.9rem] tracking-[-0.028em] leading-[1.05] text-white text-balance"
                    >
                        Guardianship Program
                    </h1>
                    <p className="mt-8 text-[17px] lg:text-[19px] text-white/85 leading-[1.75] max-w-[640px] mx-auto">
                        The assessment is the starting point. SRM can remain
                        beside the client through scope of works, tender,
                        delivery oversight and long-term care of the roof.
                    </p>
                </div>
            </div>
        </section>
    );
}

function FlexibleByDesign() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="guardian-flexible"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-16 lg:py-20">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>Flexible by Design</div>
                    <span aria-hidden className={`mx-auto mt-5 ${GOLD_RULE}`} />
                    <h2 className="mt-8 font-display font-bold text-[1.85rem] sm:text-[2.2rem] lg:text-[2.5rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                        One stage, selected stages or complete continuity.
                    </h2>
                    <p className="mt-7 text-[15.5px] lg:text-[16.5px] text-srm-body leading-[1.8]">
                        Engage SRM for one stage, selected stages or the complete
                        Guardian Program. Each engagement is separately scoped to
                        the property and the support required.
                    </p>
                </div>
            </div>
        </section>
    );
}

function Pillars() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="guardian-pillars"
            className="bg-white"
        >
            <div className="container-srm py-24 lg:py-32">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>The Four Stages</div>
                    <span aria-hidden className={`mx-auto mt-5 ${GOLD_RULE}`} />
                    <h2 className="mt-8 font-display font-bold text-[2.1rem] sm:text-[2.5rem] lg:text-[2.95rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                        One independent partner. The full journey.
                    </h2>
                    <p className="mt-7 text-[15.5px] lg:text-[16.5px] text-srm-body leading-[1.8]">
                        The Guardianship Program covers every stage from first
                        independent assessment through procurement, delivery
                        and long-term monitoring — delivered independently
                        of any repair scope, contractor commission or product
                        supply.
                    </p>
                </div>

                <div className="mt-16 lg:mt-20 grid grid-cols-1 lg:grid-cols-2 gap-7 lg:gap-8">
                    {PILLARS.map((p, i) => {
                        const PIcon = p.icon;
                        return (
                            <article
                                key={p.title}
                                data-testid={`guardian-pillar-${i}`}
                                className="reveal group relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-9 lg:p-11 shadow-[0_6px_24px_rgba(15,40,86,0.07)] transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(15,40,86,0.14)]"
                                style={{ transitionDelay: `${(i % 2) * 80}ms` }}
                            >
                                <span
                                    aria-hidden
                                    className="pointer-events-none absolute left-0 top-0 h-[3px] w-16 bg-srm-gold transition-all duration-500 group-hover:w-full"
                                />

                                <div className="flex items-start gap-5">
                                    <span className="inline-flex h-[62px] w-[62px] items-center justify-center rounded-full border border-srm-gold/45 text-srm-gold transition-all duration-500 group-hover:bg-srm-gold/10">
                                        <PIcon className="h-[26px] w-[26px]" strokeWidth={1.5} />
                                    </span>
                                    <div>
                                        <div className="font-display font-bold tracking-[0.02em] text-srm-navy text-[1.35rem] lg:text-[1.5rem] leading-tight">
                                            {p.title.includes("\u2122") ? (
                                                <GuardianMark
                                                    term={p.title.replace("\u2122", "")}
                                                    tone="navy"
                                                />
                                            ) : (
                                                p.title
                                            )}
                                        </div>
                                        <div className="mt-2 font-display font-semibold tracking-[0.2em] uppercase text-srm-navy/55 text-[11px]">
                                            {p.subtitle}
                                        </div>
                                    </div>
                                </div>

                                {p.blurb && (
                                    <p className="mt-6 text-[14.5px] lg:text-[15px] text-srm-body leading-[1.75]">
                                        {p.blurb}
                                    </p>
                                )}

                                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3.5 border-t border-srm-line pt-6">
                                    {p.items.map((it) => {
                                        const IIcon = it.icon;
                                        return (
                                            <li
                                                key={it.label}
                                                className="flex items-start gap-3 text-[14px] lg:text-[14.5px] text-srm-navy leading-[1.55]"
                                            >
                                                <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-srm-gold/10 ring-1 ring-srm-gold/40 text-srm-gold">
                                                    <IIcon className="h-3.5 w-3.5" strokeWidth={2} />
                                                </span>
                                                <span>{it.label}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function Philosophy() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="guardian-philosophy"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-24 lg:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-[42fr_58fr] gap-12 lg:gap-20 items-center">
                    <div className="reveal">
                        <div className={EYEBROW}>The Philosophy</div>
                        <span aria-hidden className={`mt-5 ${GOLD_RULE}`} />
                        <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.4rem] lg:text-[2.75rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                            A roof is an asset — treat it like one.
                        </h2>
                        <p className="mt-8 text-[15.5px] lg:text-[17px] text-srm-body leading-[1.85]">
                            Strata roofs are among the largest single assets most
                            buildings own, yet they are often the least actively
                            stewarded. The <GuardianMark /> Program brings the same
                            structured, evidence-based discipline that already applies
                            to other major building systems.
                        </p>
                        <p className="mt-6 text-[15.5px] lg:text-[17px] text-srm-body leading-[1.85]">
                            One independent partner, one continuous record — assisting
                            better-documented decisions across the life of the roof.
                        </p>
                    </div>

                    <ul className="reveal grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
                        {PROTECTS.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <li
                                    key={p.title}
                                    data-testid={`guardian-protects-${i}`}
                                    className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-7 lg:p-8 shadow-[0_6px_24px_rgba(15,40,86,0.06)]"
                                >
                                    <span
                                        aria-hidden
                                        className="pointer-events-none absolute left-0 top-0 h-[3px] w-12 bg-srm-gold"
                                    />
                                    <span className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-full border border-srm-gold/45 text-srm-gold">
                                        <Icon className="h-[22px] w-[22px]" strokeWidth={1.5} />
                                    </span>
                                    <h3 className="mt-6 font-display font-bold text-srm-navy text-[1.15rem] lg:text-[1.22rem] tracking-[-0.006em] leading-snug">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-[13.5px] lg:text-[14px] text-srm-body leading-[1.7]">
                                        {p.body}
                                    </p>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </section>
    );
}

function Method() {
    const ref = useReveal();
    const STEPS = [
        { step: "01", icon: Search,          label: "Assess",   body: "Independent roof or external building assessment establishes the actual condition." },
        { step: "02", icon: FileText,        label: "Report",   body: "Photographic findings, defect register and prioritised recommendations for the committee." },
        { step: "03", icon: ClipboardCheck,  label: "Scope",    body: "Independent scope of works defines exactly what is required \u2014 giving every contractor the same clear brief." },
        { step: "04", icon: Users,           label: "Tender",   body: "Suitable contractors invited to price the same scope, then compared apples-to-apples." },
        { step: "05", icon: HardHat,         label: "Oversee",  body: "Independent oversight of the works \u2014 materials, quality, variations, claims, close-out." },
        { step: "06", icon: CalendarClock,   label: "Monitor",  body: "Annual assessment cycles and long-term Guardianship of the roof asset." },
    ];
    return (
        <section
            ref={ref}
            data-testid="guardian-method"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-24 lg:py-32">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>Assess &middot; Report &middot; Scope &middot; Tender &middot; Oversee &middot; Monitor</div>
                    <span aria-hidden className={`mx-auto mt-5 ${GOLD_RULE}`} />
                    <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.4rem] lg:text-[2.75rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                        The six-step Guardianship method.
                    </h2>
                </div>

                <div className="relative mt-16 lg:mt-20">
                    {/* Connector line: horizontal on lg+, vertical on smaller screens */}
                    <div
                        aria-hidden
                        className="hidden lg:block absolute top-[34px] left-[8%] right-[8%] h-px bg-srm-gold/30"
                    />
                    <div
                        aria-hidden
                        className="lg:hidden absolute top-0 bottom-0 left-[34px] w-px bg-srm-gold/30"
                    />

                    <ol className="relative grid grid-cols-1 lg:grid-cols-6 gap-10 lg:gap-6">
                        {STEPS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <li
                                    key={s.label}
                                    data-testid={`guardian-step-${i}`}
                                    className="reveal flex lg:flex-col items-start lg:items-center gap-5 lg:gap-0 text-left lg:text-center"
                                    style={{ transitionDelay: `${i * 80}ms` }}
                                >
                                    <div className="relative shrink-0">
                                        <span className="relative inline-flex h-[68px] w-[68px] items-center justify-center rounded-full bg-white ring-1 ring-srm-gold/40 shadow-[0_8px_22px_rgba(15,40,86,0.10)]">
                                            <Icon className="h-[26px] w-[26px] text-srm-gold" strokeWidth={1.5} />
                                        </span>
                                        <span className="absolute -top-2 -right-2 inline-flex h-6 px-2 items-center justify-center rounded-sm bg-srm-navy text-white font-display font-bold tracking-[0.18em] text-[10px]">
                                            {s.step}
                                        </span>
                                    </div>
                                    <div className="min-w-0 lg:mt-6">
                                        <h3 className="font-display font-bold text-srm-navy text-[1.1rem] lg:text-[1.2rem] tracking-[-0.01em] uppercase leading-none">
                                            {s.label}
                                        </h3>
                                        <span
                                            aria-hidden
                                            className="mt-3.5 lg:mx-auto block h-[2px] w-10 bg-srm-gold"
                                        />
                                        <p className="mt-4 text-[13px] lg:text-[13.5px] text-srm-body leading-[1.65] max-w-[14rem] lg:max-w-none">
                                            {s.body}
                                        </p>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </div>
            </div>
        </section>
    );
}

function FinalCTA() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="guardian-cta"
            className="relative overflow-hidden"
            style={{ backgroundColor: "#163E7A" }}
        >
            <div
                aria-hidden
                className="pointer-events-none absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full"
                style={{
                    background:
                        "radial-gradient(circle, rgba(201,165,92,0.10) 0%, rgba(201,165,92,0) 70%)",
                }}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute -bottom-40 -left-20 h-[420px] w-[420px] rounded-full"
                style={{
                    background:
                        "radial-gradient(circle, rgba(201,165,92,0.07) 0%, rgba(201,165,92,0) 70%)",
                }}
            />
            <div className="container-srm relative py-24 lg:py-32">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <span aria-hidden className={`mx-auto ${GOLD_RULE}`} />
                    <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.5rem] lg:text-[3rem] tracking-[-0.024em] leading-[1.1] text-white text-balance">
                        Discuss the{" "}
                        <GuardianMark
                            term="Guardian Program"
                            tone="light"
                            variant="signature"
                            className="text-srm-gold"
                        />{" "}
                        for your building.
                    </h2>
                    <p className="mt-8 text-[15.5px] lg:text-[17.5px] text-white/80 leading-[1.85] max-w-2xl mx-auto">
                        Every program is shaped to the roof, the committee and the
                        building. Tell us what you&apos;re looking after — we&apos;ll
                        recommend a suitable starting point.
                    </p>
                    <div className="mt-12 flex justify-center">
                        <Link
                            to="/contact"
                            data-testid="guardian-cta-primary"
                            className="group inline-flex items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[12px] font-semibold tracking-[0.2em] uppercase px-10 h-[56px] border border-srm-gold shadow-[0_10px_30px_rgba(201,165,92,0.30)] hover:bg-srm-goldDark hover:border-srm-goldDark hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(201,165,92,0.40)] transition-all duration-300"
                        >
                            Discuss the Guardian Program
                            <ArrowRight
                                className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1"
                                strokeWidth={2}
                            />
                        </Link>
                    </div>

                    <div className="mt-8 inline-flex items-center gap-2 text-white/60 text-[12px] tracking-[0.16em] uppercase">
                        <CheckCircle2 className="h-3.5 w-3.5 text-srm-gold" strokeWidth={2} />
                        Independent · Evidence-based · Committee-first
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function GuardianProgram() {
    usePageMeta({
        title: "Guardian Program \u2014 Strata Roof Asset Management | SRM",
        description:
            "SRM's Guardian Program provides independent strata roof asset management: assessment and reporting, scope of works and tender support, delivery oversight and ongoing roof monitoring for body corporate planning.",
        path: "/guardian-program",
        breadcrumbs: [
            { name: "Home", url: "/" },
            { name: "Guardian Program", url: "/guardian-program" },
        ],
    });

    return (
        <div data-testid="guardian-program-page" className="min-h-screen bg-white">
            <Navbar />
            <main id="main">
                <Hero />
                <FlexibleByDesign />
                <SingleReviewBlock
                    review={REVIEWS.lloyd}
                    heading="From assessment to ongoing support"
                    testid="guardian-review"
                />
                <Pillars />
                <Philosophy />
                <FinalCTA />
            </main>
            <Footer />
        </div>
    );
}
